export const CABLES_RUNTIME_URL =
  "https://d2my2wpsc41l6t.cloudfront.net/modernexpo/js/combined-20260326-161733.gz.js";

const RUNTIME_SCRIPT_ATTRIBUTE = "data-modern-expo-cables-runtime";
let cablesRuntimePromise;

function findRuntimeScript() {
  return Array.from(
    document.querySelectorAll(
      `script[${RUNTIME_SCRIPT_ATTRIBUTE}], script[src="${CABLES_RUNTIME_URL}"]`
    )
  ).find((script) => script.dataset.modernExpoCablesRuntimeFailed !== "true");
}

function waitForRuntimeScript(script) {
  if (window.CABLES) return Promise.resolve(window.CABLES);

  return new Promise((resolve, reject) => {
    script.addEventListener("load", () => resolve(window.CABLES), { once: true });
    script.addEventListener(
      "error",
      () => {
        script.dataset.modernExpoCablesRuntimeFailed = "true";
        reject(new Error("CABLES runtime failed to load"));
      },
      { once: true }
    );
  });
}

function createRuntimeScript() {
  const script = document.createElement("script");
  script.src = CABLES_RUNTIME_URL;
  script.async = true;
  script.setAttribute(RUNTIME_SCRIPT_ATTRIBUTE, "");

  const loadPromise = waitForRuntimeScript(script);
  document.head.appendChild(script);
  return loadPromise;
}

export function installCablesCompatibilityOps(cables = window.CABLES, ops = window.Ops) {
  const compareOps = ops?.Math?.Compare;

  if (!cables?.Op || !compareOps || typeof compareOps.BetweenEquals === "function") {
    return false;
  }

  compareOps.BetweenEquals = class BetweenEquals extends cables.Op {
    constructor(...args) {
      super(...args);
      this.attachments = {};

      const value = this.inValue("value", 2);
      const firstBoundary = this.inValue("number1", 1);
      const secondBoundary = this.inValue("number2", 3);
      const result = this.outNumber("result");
      const updateResult = () => {
        const minimum = Math.min(firstBoundary.get(), secondBoundary.get());
        const maximum = Math.max(firstBoundary.get(), secondBoundary.get());
        result.set(value.get() >= minimum && value.get() <= maximum);
      };

      value.onChange = updateResult;
      firstBoundary.onChange = updateResult;
      secondBoundary.onChange = updateResult;
      updateResult();
    }
  };

  return true;
}

export function ensureCablesRuntime() {
  if (window.CABLES) {
    installCablesCompatibilityOps();
    return Promise.resolve(window.CABLES);
  }

  if (!cablesRuntimePromise) {
    const existingScript = findRuntimeScript();

    cablesRuntimePromise = (
      existingScript ? waitForRuntimeScript(existingScript) : createRuntimeScript()
    )
      .then((cables) => {
        if (!cables) throw new Error("CABLES runtime did not expose window.CABLES");
        installCablesCompatibilityOps(cables, window.Ops);
        return cables;
      })
      .catch((error) => {
        cablesRuntimePromise = null;
        throw error;
      });
  }

  return cablesRuntimePromise;
}

export function runAfterWebflowInteractions(callback, delay = 1500) {
  let isScheduled = false;
  const schedule = () => {
    if (isScheduled) return;
    isScheduled = true;
    window.setTimeout(callback, delay);
  };

  if (
    document.documentElement.classList.contains("w-mod-ix3") ||
    document.readyState === "complete"
  ) {
    schedule();
    return;
  }

  window.addEventListener("__wf_ix3_ready", schedule, { once: true });
  window.addEventListener("load", schedule, { once: true });
}

export function runWhenIdle(callback) {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback, { timeout: 500 });
    return;
  }

  window.setTimeout(callback, 0);
}

export function observeProximity(element, onEnter, onExit, margin = "50% 0px") {
  if (!element) return null;

  if (typeof window.IntersectionObserver !== "function") {
    onEnter();
    return null;
  }

  let isInside = false;
  const observer = new window.IntersectionObserver(
    (entries) => {
      const isIntersecting = Boolean(entries[0]?.isIntersecting);

      if (isIntersecting && !isInside) {
        isInside = true;
        onEnter();
      } else if (!isIntersecting && isInside) {
        isInside = false;
        onExit();
      }
    },
    {
      root: null,
      rootMargin: margin,
      threshold: 0,
    }
  );

  observer.observe(element);
  return observer;
}
